import { Components, Theme, ThemeOptions } from '@mui/material/styles';
import { SizeMap } from '@/themes';

const getToggleViewTheme = (theme: Theme): ThemeOptions => {
  // ToggleView Size Map - Matches Button component sizing
  // Can be overridden by defining theme.size.toggleView in muiTheme.ts
  const toggleViewSize: SizeMap = {
    s: {
      padding: theme.unit(1.5, 2.5),
      borderRadius: theme.unit(1.5),
      ...theme.typography.M3.label.mediumProminent,
    },
    m: {
      padding: theme.unit(2, 4),
      borderRadius: theme.unit(2.5),
      ...theme.typography.M3.label.large,
    },
    l: {
      padding: theme.unit(2.5, 6),
      borderRadius: theme.unit(3),
      ...theme.typography.M3.label.large,
    },
    xl: {
      padding: theme.unit(2.5, 7),
      borderRadius: theme.unit(3.5),
      ...theme.typography.M3.title.medium,
    },
  };

  const toggleViewIconSize: SizeMap = theme.size?.toggleViewIcon || {
    s: {
      fontSize: '14px',
      width: '14px',
      height: '14px',
    },
    m: {
      fontSize: '18px',
      width: '18px',
      height: '18px',
    },
    l: {
      fontSize: '18px',
      width: '18px',
      height: '18px',
    },
    xl: {
      fontSize: '22px',
      width: '22px',
      height: '22px',
    },
  };

  const toggleButtonGroupTheme: Components<Theme>['MuiToggleButtonGroup'] = {
    defaultProps: {
      size: 'm',
    },
    styleOverrides: {
      root: {
        backgroundColor: theme.palette.M3.sys.light.background,
        border: `1px solid ${theme.palette.M3.sys.light['outline 3']}`,
        display: 'inline-flex',
        width: 'auto',
        minWidth: 'auto',
        maxWidth: 'fit-content',
        flex: 'none',
        alignSelf: 'flex-start',
        overflow: 'hidden',
      },
      grouped: {
        margin: 0,
        border: 0,
        '&:not(:first-of-type)': {
          marginLeft: 0,
        },
      },
    },
    variants: [
      // Size variants for ToggleButtonGroup
      {
        props: { size: 's' },
        style: {
          borderRadius: theme.unit(1.5),
        },
      },
      {
        props: { size: 'm' },
        style: {
          borderRadius: theme.unit(2.5),
        },
      },
      {
        props: { size: 'l' },
        style: {
          borderRadius: theme.unit(3),
        },
      },
      {
        props: { size: 'xl' },
        style: {
          borderRadius: theme.unit(3.5),
        },
      },
    ],
  };

  const toggleButtonTheme: Components<Theme>['MuiToggleButton'] = {
    styleOverrides: {
      root: {
        border: 'none',
        borderColor: 'transparent',
        textTransform: 'none',
        fontWeight: 600,
        color: theme.palette.M3.sys.light['on-surface-variant'],
        backgroundColor: 'transparent',
        '&:first-of-type': {
          borderRight: `1px solid ${theme.palette.M3.sys.light['outline 3']}`,
        },
        '& .MuiSvgIcon-root': {
          color: theme.palette.M3.sys.light['on-surface-variant'],
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.M3.sys.light['background 2'],
          color: theme.palette.M3.sys.light['on-surface'],
          '& .MuiSvgIcon-root': {
            color: theme.palette.M3.sys.light['on-surface-variant'],
          },
          '&:hover': {
            backgroundColor: theme.palette.M3.sys.light['background 2'],
          },
        },
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
    variants: [
      // Size variants - s
      {
        props: { size: 's' },
        style: {
          ...toggleViewSize.s,
          '&:first-of-type': {
            borderRadius: theme.unit(1.5, 0, 0, 1.5),
          },
          '&:last-of-type': {
            borderRadius: theme.unit(0, 1.5, 1.5, 0),
          },
          '& .MuiSvgIcon-root': {
            ...toggleViewIconSize.s,
          },
        },
      },
      // Size variants - m
      {
        props: { size: 'm' },
        style: {
          ...toggleViewSize.m,
          '&:first-of-type': {
            borderRadius: theme.unit(2.5, 0, 0, 2.5),
          },
          '&:last-of-type': {
            borderRadius: theme.unit(0, 2.5, 2.5, 0),
          },
          '& .MuiSvgIcon-root': {
            ...toggleViewIconSize.m,
          },
        },
      },
      // Size variants - l
      {
        props: { size: 'l' },
        style: {
          ...toggleViewSize.l,
          '&:first-of-type': {
            borderRadius: theme.unit(3, 0, 0, 3),
          },
          '&:last-of-type': {
            borderRadius: theme.unit(0, 3, 3, 0),
          },
          '& .MuiSvgIcon-root': {
            ...toggleViewIconSize.l,
          },
        },
      },
      // Size variants - xl
      {
        props: { size: 'xl' },
        style: {
          ...toggleViewSize.xl,
          '&:first-of-type': {
            borderRadius: theme.unit(3.5, 0, 0, 3.5),
          },
          '&:last-of-type': {
            borderRadius: theme.unit(0, 3.5, 3.5, 0),
          },
          '& .MuiSvgIcon-root': {
            ...toggleViewIconSize.xl,
          },
        },
      },
    ],
  };

  return {
    components: {
      MuiToggleButtonGroup: toggleButtonGroupTheme,
      MuiToggleButton: toggleButtonTheme,
    },
    size: { toggleView: toggleViewSize, toggleViewIcon: toggleViewIconSize },
  };
};

export default getToggleViewTheme;
